/**
 * Le centre de notifications (docs/14 §8).
 *
 * Deux états vides distincts — « aucune notification » et « aucune non lue » —
 * parce qu'ils appellent des gestes opposés : le premier dit qu'il ne s'est rien
 * passé, le second qu'on a déjà tout lu. Une seule phrase pour les deux ferait
 * croire à un compte inactif à qui vient de tout parcourir.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useMarkAllRead,
  useMarkRead,
  useNotifications,
} from "@/features/notifications/api/useNotifications";
import { NotificationItem } from "@/features/notifications/components/NotificationItem";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function NotificationsPage() {
  const { t } = useTranslation("notifications");
  const [unreadOnly, setUnreadOnly] = useState(false);
  const { data, isPending, isError, error } = useNotifications(unreadOnly);
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();

  return (
    <section className="mx-auto max-w-3xl px-4 py-8">
      <header className="mb-6 flex items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">{t("title")}</h1>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
            <input
              type="checkbox"
              checked={unreadOnly}
              onChange={(event) => {
                setUnreadOnly(event.target.checked);
              }}
            />
            {t("list.unreadOnly")}
          </label>
          <Button
            variant="secondary"
            onClick={() => {
              markAllRead.mutate();
            }}
            disabled={markAllRead.isPending || (data?.unread_count ?? 0) === 0}
          >
            {t("bell.markAllRead")}
          </Button>
        </div>
      </header>

      {isPending ? <Skeleton className="h-40 w-full" label={t("title")} /> : null}
      {isError ? <ErrorState error={error} /> : null}

      {data !== undefined ? (
        <Card className="overflow-hidden p-0">
          {data.items.length === 0 ? (
            <p className="p-6 text-sm text-slate-500 dark:text-slate-400">
              {unreadOnly ? t("list.emptyUnread") : t("list.empty")}
            </p>
          ) : (
            <ul>
              {data.items.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={(id) => {
                    markRead.mutate(id);
                  }}
                />
              ))}
            </ul>
          )}
        </Card>
      ) : null}
    </section>
  );
}
